import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IncomeAttributeState } from "../../../redux/reducers/getIncomeAttributes/getIncomeAttributesReducer";
import { useTranslation } from "react-i18next";
import { getLookups } from "../../../redux/actions/lookups/lookups";
import { ProfileOverviewState } from "../../../redux/reducers/getProfile/getProfileOverviewReducer";
import { SaveDataState } from "../../../redux/reducers/getSaveData/saveDataReducer";
import { getIncomeAttributesBySnapshotId, putIncomeAttributesBySnapshotId } from "../../../redux/actions/incomeAttributes/incomeAttributes";
import IncomeAttributeLookupObject from "../../../types/lookups/IncomeAttributeLookupObject";
import { SaveResultState } from "../../../redux/reducers/getSaveData/saveResultReducer";
import { IncomeAttributeLookupCategories } from "../../../types/lookups/IncomeAttributeLookupState";
import Total from "../../../components/common/Total/Total";
import calculateIncomeAttributesTotal from "../../../utils/calculateIncomeAttributesTotal";
import "../PIE.css";
import { useFormErrorContext } from "../../../utils/context/FormErrorContext";
import { useNavigation } from "../../../utils/context/NavigationContext";
import ErrorMessages from "../../../components/common/ErrorMessages/ErrorMessages";
import { accordionDisplay, getCurrentCampaignYear, getLastYearSubmittedCampaign, shouldDisplayPreviousTwoYearAttributes, shouldDisplayPreviousYearAttributes } from "../../../utils/previousIncomeAttributes";
import { MANAGE_PREVIOUS_INCOME_ATTRIBUTES_BY_SNAPSHOT_ID } from "../../../redux/actionTypes/actionsTypes";
import IncomeAttributeTableTab from "./IncomeAttributeTableTab";
import PreviousCampaignIncomeAttributes from "../../../components/common/PreviousCampaignIncomeAttributes/PreviousCampaignIncomeAttributes";
import { selectGetIncomeAttributes, selectLookups, selectMenu, selectPreviousIncomeAttributes, selectProfileGetOverview, selectSaveData, selectSaveResult } from "../../../redux/selectors/exportSelectorPaths";
import AccordionItem, { DefaultAccordionProviderOptions } from "../../../components/common/AccordionItem/AccordionItem";
import { ControlledAccordion, useAccordionProvider } from "@szhsin/react-accordion";

export default function IncomeAttributeTable(): JSX.Element {
  const { t } = useTranslation(["pie", "navigation"]);
  const dispatch = useDispatch();
  const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
  const incomeAttributesState: IncomeAttributeState = useSelector(selectGetIncomeAttributes);
  const previousIncomeAttributesState: IncomeAttributeState = useSelector(selectPreviousIncomeAttributes)
  const lookupState = useSelector(selectLookups);
  const menuState = useSelector(selectMenu);
  const saveData: SaveDataState = useSelector(selectSaveData);
  const saveResultState: SaveResultState = useSelector(selectSaveResult);
  const activeMenuName = menuState.activeItem.name;
  const replacementReserveAttributeCode = "50";
  const atLeastOne = t(activeMenuName + ".atLeastOne") === "true";
  const [lookups, setLookups] = useState<IncomeAttributeLookupObject[]>([]);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const { direction } = useNavigation()
  const { errors } = useFormErrorContext()
  const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)
  const isInternal = (sessionStorage.getItem("isInternalUser") as string) === "true"
  const currentCampaignYear = getCurrentCampaignYear()

  useEffect(() => {
    if (!lookupState.success && !lookupState.loading) {
      const lookupType = profileOverview.profile.profile_type_code.toLowerCase() + "-income-attributes";
      dispatch(getLookups(lookupType));
    }
  }, [dispatch, lookupState.loading, lookupState.success, profileOverview.profile.profile_type_code]);

  useEffect(() => {
    let lookups = [] as IncomeAttributeLookupObject[];
    if (lookupState.success) {
      const categorys = Object.keys(lookupState.lookups);
      for (let c = 0; c < categorys.length; c++) {
        const category = categorys[c] as IncomeAttributeLookupCategories;
        const pages = Object.keys(lookupState.lookups[category]);
        if (pages.includes(activeMenuName)) {
          lookups = lookupState.lookups[category][activeMenuName] as IncomeAttributeLookupObject[];
          break;
        }
      }
      setLookups(lookups);
    }
  }, [activeMenuName, lookupState.lookups, lookupState.success]);

  useEffect(() => {
    if (!incomeAttributesState.loading && incomeAttributesState.success && lookups.length > 0) {
      setTotal(calculateIncomeAttributesTotal(lookups, incomeAttributesState.data as Record<string, unknown>[]))
    }
  }, [incomeAttributesState, lookups])

  const getIncomeAttributes = () => {
    if ((!incomeAttributesState.loading && !incomeAttributesState.success)
      || incomeAttributesState.snapshotId !== Number(sessionStorage.getItem('currentSnapshotId') as string)) {
      dispatch(getIncomeAttributesBySnapshotId(sessionStorage.getItem('currentSnapshotId') as string));
    }

    const campaign = getLastYearSubmittedCampaign(profileOverview.profile)

    if (campaign && ((!previousIncomeAttributesState.loading && !previousIncomeAttributesState.success) || previousIncomeAttributesState.snapshotId !== campaign.currentSnapshotId)) {
      dispatch(getIncomeAttributesBySnapshotId(campaign.currentSnapshotId, MANAGE_PREVIOUS_INCOME_ATTRIBUTES_BY_SNAPSHOT_ID))
    }
  }

  useEffect(() => {
    getIncomeAttributes()
    // eslint-disable-next-line
  }, []);

  const findAttributeData = (attributeCode: string) => {
    let incomeAttributesData: Record<string, unknown> | undefined = undefined;
    if (incomeAttributesState.data) {
      incomeAttributesData = (incomeAttributesState.data as Record<string, unknown>[]).find(({ code }) => code === attributeCode);
    }
    return incomeAttributesData;
  };

  // const validate = () => {
  //   let valid = true;
  //   // if (sessionStorage.getItem("isInternalUser")) {
  //   //   valid = true;
  //   // } else {
  //   //   const errs: Record<string, string> = {};
  //   //   if (atLeastOne) {
  //   //     errs["atLeastOne"] = "";
  //   //     if (total) {
  //   //       valid = total > 0;
  //   //     } else {
  //   //       valid = false
  //   //     }
  //   //     if (!valid) {
  //   //       errs["atLeastOne"] = 'validation.atLeastOne';
  //   //     }
  //   //   }

  //   //   const requiredAttributeCodes: string[] = required.endsWith(".required") ? [] : JSON.parse(required);
  //   //   if (requiredAttributeCodes.length > 0) {
  //   //     requiredAttributeCodes.forEach((code) => {
  //   //       errs[code] = "";
  //   //       const attributeData = findAttributeData(code);
  //   //       if (!attributeData || !attributeData.value) {
  //   //         valid = false;
  //   //         errs[code] = 'validation.' + code;
  //   //       }
  //   //     });
  //   //   }
  //   //   if (showError) {
  //   //     setErrs(errs);
  //   //   }
  //   // }

  //   if (isReadOnly) {
  //     valid = true;
  //   }
  //   return valid;
  // }

  // useEffect(() => {
  //   if (!incomeAttributesState.loading && incomeAttributesState.success && !validate(false) && !saveData.changesPending) {
  //     dispatch(setChangesMade());
  //   }
  //   // eslint-disable-next-line
  // }, [dispatch, incomeAttributesState])


  const getPreviousTotals = useCallback((year: string) => {
    const sessionStoragePreviousSnapshotData = sessionStorage.getItem(isInternal ? "previousSnapshotDataInternal" + year : "previousSnapshotData")
    if (sessionStoragePreviousSnapshotData !== null && sessionStoragePreviousSnapshotData !== undefined) {
      let previousSnapshotData = { incomeAttribute: [] }
      previousSnapshotData = JSON.parse(sessionStoragePreviousSnapshotData)
      if (previousSnapshotData.incomeAttribute) {
        return calculateIncomeAttributesTotal(lookups,
          previousSnapshotData.incomeAttribute as Record<string, unknown>[])
      }
    }
    return 0
  }, [lookups, isInternal])

  useEffect(() => {
    if (saveData.saving) {
      const incomeAttributes = (incomeAttributesState.data as Record<string, unknown>[]).filter((data) => data.value !== null || data.description !== null || data.percent !== null);
      const triggerSpectral = isInternal || direction !== "backward"
      dispatch(putIncomeAttributesBySnapshotId(Number(sessionStorage.getItem("currentSnapshotId") as string), {
        "attributes": incomeAttributes,
        "spectral_section": profileOverview.profile.profile_type_code.toLowerCase() + "-" + menuState.activeItem.name,
        "trigger_spectral": triggerSpectral,
        "is_internal": isInternal
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, incomeAttributesState.data, menuState.activeItem.name, saveData.saving])

  useEffect(() => {
    if (saveResultState.status === 200) {
      dispatch(getIncomeAttributesBySnapshotId(Number(sessionStorage.getItem("currentSnapshotId") as string)));
    }
  }, [dispatch, saveResultState]);

  const findLookupData = (attributeCode: string) => {
    let lookupData: IncomeAttributeLookupObject | undefined;
    if (lookups.length > 0) {
      lookupData = lookups.find(({ incomeAttributeCode }) => incomeAttributeCode === attributeCode);
    }
    return lookupData;
  };

  const handleCurrencyChange = (newAmt: number | undefined | null, name: string, _oldAmt: number | undefined | null) => {
    const idx = name.lastIndexOf("-");
    if (idx !== -1) {
      const code = name.substring(idx + 1);
      const incomeAttributesData = findAttributeData(code);
      const na = (newAmt === undefined || newAmt === null) ? null : newAmt
      if (incomeAttributesData) {
        incomeAttributesData["value"] = na;
      } else {
        const lookupData = findLookupData(code);
        (incomeAttributesState.data as Record<string, unknown>[]).push({
          snapshotId: Number(sessionStorage.getItem("currentSnapshotId") as string),
          code: code,
          description: null,
          value: na,
          type: lookupData?.incomeAttributeTypeCode,
          percent: null,
          year: null,
        });
      }
      setTotal(calculateIncomeAttributesTotal(lookups.filter((l) => l.incomeAttributeCode !== replacementReserveAttributeCode),
        incomeAttributesState.data as Record<string, unknown>[]))
    }
  }

  const handleCommentChange = (value: string, name: string) => {
    const idx = name.lastIndexOf("-");
    if (idx !== -1) {
      const code = name.substring(idx + 1);
      const incomeAttributesData = findAttributeData(code);
      const v = value.trim() === "" ? null : value.trim();
      if (incomeAttributesData) {
        incomeAttributesData["description"] = v;
      } else {
        const lookupData = findLookupData(code);
        (incomeAttributesState.data as Record<string, unknown>[]).push({
          snapshotId: Number(sessionStorage.getItem("currentSnapshotId") as string),
          code: code,
          description: v,
          value: null,
          type: lookupData?.incomeAttributeTypeCode,
          percent: null,
          year: null
        });
      }
    }
  }

  return (
    <>
      <div className="full-width">
        <div className="content-container">
          <ErrorMessages errors={errors} />
          {atLeastOne && <p className="imp-note">{t(activeMenuName + ".atLeaseOneText")}</p>}
          <p>{t(activeMenuName + ".note")}</p>
          {
            (!lookupState.loading && !incomeAttributesState.loading && !previousIncomeAttributesState.loading) &&
            <div className="accordion-root">
              <div className={accordionDisplay(isInternal, "accordion")}>
                <ControlledAccordion providerValue={providerValue}>
                  <AccordionItem header={t(activeMenuName, { ns: "navigation" })}>
                    <IncomeAttributeTableTab
                      incomeAttributes={(incomeAttributesState.data && (incomeAttributesState.data as Record<string, unknown>[]).length > 0) ? (incomeAttributesState.data as Record<string, unknown>[]) : undefined}
                      handleCommentChange={handleCommentChange}
                      handleCurrencyChange={handleCurrencyChange}
                      lookups={lookups} />
                  </AccordionItem>
                </ControlledAccordion>
              </div>
              {shouldDisplayPreviousYearAttributes() ? <PreviousCampaignIncomeAttributes lookups={lookups} tabComponent='incomeLoss' forcedTab year={String(getCurrentCampaignYear() - 1)} /> : <></>}
              {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <PreviousCampaignIncomeAttributes lookups={lookups} tabComponent='incomeLoss' forcedTab year={String(getCurrentCampaignYear() - 2)} /> : <></>}
            </div>
          }
        </div>

        <div className='total-container'>
          <div className="accordion-root">
            <div className={accordionDisplay(isInternal, "accordion")}>
              <Total text={t(activeMenuName + '.total')} value={total} className={activeMenuName === "rentalLoss" ? "secondary" : ""} />
            </div>
            {shouldDisplayPreviousYearAttributes() ? <div className={accordionDisplay(isInternal, "right")}>
              <Total text={t(activeMenuName + '.total')} value={getPreviousTotals(String(currentCampaignYear - 1))} className={activeMenuName === "rentalLoss" ? "secondary" : ""} />
            </div> : <></>}
            {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <div className={accordionDisplay(isInternal, "right")}>
              <Total text={t(activeMenuName + '.total')} value={getPreviousTotals(String(currentCampaignYear - 2))} className={activeMenuName === "rentalLoss" ? "secondary" : ""} />
            </div> : <></>}
          </div>
        </div>
      </div>
    </>
  );
}
